// Aviso de una escritura de dinero en duda (o caducada) y sus salidas, para sus diálogos.
import type { ReactNode } from 'react';
import { Button } from '@/kit';
import { cn } from '@/lib/cn';
import { STATUS_PILL_TONE_CLASS } from '@/shared/patterns/Pills';

/**
 * En duda con lo enviado a la vista, en edición, sin lo enviado (otra pestaña o tras recargar) o
 * enviándose desde otra pestaña.
 */
export type DoubtMode = 'retry' | 'editing' | 'recapture' | 'elsewhere';

const TEXT: Record<DoubtMode, string> = {
    retry: 'Puede que ya haya quedado registrado. Reintenta con los mismos datos; no se duplica.',
    editing: 'Si lo anterior ya quedó registrado, estos datos no se registran y te lo avisamos.',
    recapture:
        'Puede que ya haya quedado registrado. Escribe de nuevo el motivo y envía: no se duplica.',
    elsewhere:
        'Se está enviando desde otra pestaña. Si ya la cerraste, escribe de nuevo el motivo y envía: no se duplica.',
};
/** Qué aviso toca; mientras se envía no hay aviso. */
export function doubtModeOf(state: {
    inDoubt: boolean;
    busy: boolean;
    elsewhere: boolean;
    recapture: boolean;
    editing: boolean;
}): DoubtMode | null {
    if (!state.inDoubt || state.busy) return null;
    if (state.elsewhere) return 'elsewhere';
    if (state.recapture) return 'recapture';
    return state.editing ? 'editing' : 'retry';
}

const EXPIRED =
    'Ese intento ya no se puede confirmar; revisa el pendiente antes de registrar de nuevo.';

interface WriteDoubtNoticeProps {
    /** `null` sin duda viva. */
    mode: DoubtMode | null;
    expired: boolean;
    disabled: boolean;
    onEdit: () => void;
    /** Más contexto y salidas, por ejemplo el pendiente actual. */
    extra?: ReactNode;
}

export function WriteDoubtNotice({
    mode,
    expired,
    disabled,
    onEdit,
    extra,
}: WriteDoubtNoticeProps) {
    if (mode === null && !expired) return null;
    return (
        <div
            className={cn(
                'flex flex-col gap-1.5 rounded-md border px-2.5 py-1.5 text-[11.5px]',
                STATUS_PILL_TONE_CLASS.warning,
            )}
        >
            <output>{mode === null ? EXPIRED : TEXT[mode]}</output>
            {mode === 'retry' && (
                <div>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={disabled}
                        onClick={onEdit}
                    >
                        Usar otros datos
                    </Button>
                </div>
            )}
            {extra}
        </div>
    );
}
